// src/components/CheckOut.js
import React, { useEffect, useState } from "react";
import Topbar from "./user/Navigations/Topbar";
import MainBanner from "./user/MainBanner";
import NavbarWithCat from "./user/Navigations/NavbarWithCat";
import Footer from "./Footer";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "./toast/ToastProvider";
import { getCartProductsByCustomerId } from "../service/CartService";
import { fetchProductImage } from "../service/ProductImageService";
import { fetchAllCustomerProduct } from "../service/ProductService";
import { apiUrl } from "../config";
import {
  createCustomerAddress,
  fetchCustomerAddressByCustId,
} from "../service/CustomerAddressServices";
import { formatINR } from "../App";
import { useSelector } from "react-redux";
import { createOrder, sendEmail } from "../service/OrderService";
import { clearCart } from "../Redux/Slices/cartSlices";
import { getWishlistProductsByCustomerId } from "../service/WishlistService";
import "../styles/userLogin.css";

const CheckOut = () => {
  const countryState = {
    India: ["Tamil Nadu", "Kerala", "Karnataka"],
    USA: ["California", "Texas", "New York"],
    UK: ["England", "Scotland", "Wales"],
  };

  const stateCity = {
    "Tamil Nadu": ["Trichy", "Chennai", "Madurai"],
    Kerala: ["Kochi", "Thiruvananthapuram", "Kozhikode"],
    Karnataka: ["Bengaluru", "Mysuru", "Mangalore"],
    California: ["Los Angeles", "San Francisco", "San Diego"],
    Texas: ["Houston", "Dallas", "Austin"],
    "New York": ["New York City", "Buffalo", "Albany"],
    England: ["London", "Manchester", "Liverpool"],
    Scotland: ["Edinburgh", "Glasgow"],
    Wales: ["Cardiff", "Swansea"],
  };

  const authUser = useSelector((state) => state.auth.authUser);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [zip, setZip] = useState("");
  const [address, setAddress] = useState("");
  const [addressData, setAddressData] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const cartData = await getCartProductsByCustomerId(authUser.id);
      const productImageData = await fetchProductImage();
      const productsData = await fetchAllCustomerProduct();
      const addressData = await fetchCustomerAddressByCustId(authUser.id);
      const response = await getWishlistProductsByCustomerId(authUser.id);
      setWishlist(response);
      setAddressData(addressData);

      const mergedData = cartData.map((cartItem) => {
        const images = productImageData.filter(
          (img) => img.productId === cartItem.productId,
        );
        const productDetails = productsData.find(
          (p) => p.id === cartItem.productId,
        );
        return {
          ...cartItem,
          images,
          ornamentPrice: productDetails?.ornamentPrice ?? 0,
          priceBeforeGst: productDetails?.priceBeforeGst ?? 0,   
    discount: productDetails?.discount ?? 0,    
          name: productDetails?.name ?? "Unknown",
          mrp: productDetails?.mrp ?? 0,
          rating: productDetails?.rating ?? 4,
          quantity: cartItem.quantity,
        };
      });

      setCart(mergedData);
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast("Error in fetching", "ERROR");
    } finally {
      setIsLoading(false);
    }
  };

  const clearInputs = () => {
    setFirstName("");
    setLastName("");
    setSelectedCountry("");
    setSelectedState("");
    setSelectedCity("");
    setZip("");
    setAddress("");
  };
  const handleSaveAddress = async () => {
    if (
      !firstName ||
      !lastName ||
      !zip ||
      !selectedCountry ||
      !selectedState ||
      !selectedCity ||
      !address
    ) {
      toast("Please fill up the address form", "ERROR");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: firstName + " " + lastName,
        customerId: authUser.id,
        address,
        city: selectedCity,
        state: selectedState,
        country: selectedCountry,
        zipCode: zip,
      };

      const res = await createCustomerAddress(payload);

      // Refresh address data after saving
      const updatedAddresses = await fetchCustomerAddressByCustId(authUser.id);
      setAddressData(updatedAddresses);
      setSelectedAddressId(res.id); // Select the newly saved address
      clearInputs();
      toast("Address saved successfully!", "SUCCESS");
    } catch (err) {
      console.error("Save Address Error:", err);
      toast("Failed to save address. Please try again.", "ERROR");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    setIsLoading(true);
    try {
      let payload;

      if (selectedAddressId === null) {
        toast("Please select or save an address", "ERROR");
        return;
      }

      const selectedAddr = addressData?.find(
        (addr) => addr.id === selectedAddressId,
      );
      if (!selectedAddr) {
        toast("Please select a valid address", "ERROR");
        return;
      }

      payload = {
        ...selectedAddr,
        customer_id: authUser.id,
      };

      const orderItems = cart.map((item) => {
        const discountPrices = getDiscountPrice(item);
      return{
          productId: item.productId,
        weight: item.weight || 0,
        goldRate: item.goldRate || 0,
        productRate: item.goldRate * item.weight || 0,
        makingCharge: item.makingCharge || 0,
        wastageCharge: item.wastageCharge || 0,
        gst:item.gst ||0,
        discount: item.discount || 0,
        totalAmount: discountPrices * (item.quantity || 1),
        productQuantity: item.quantity || 1,
        status: "ordered",
      }
      });

      const res3 = await createOrder({
        customerId: authUser.id,
        totalQuantity: cart.reduce(
          (sum, item) => sum + (item.quantity || 1),
          0,
        ),
        totalAmount: totalPrice,
        name: authUser.name,
        email: authUser.email,
        phone: authUser.phone,
        items: orderItems,
      });

      toast("Order placed successfully!", "SUCCESS");

      navigate(`/order-confirm/${res3.orderId}`);
    } catch (err) {
      console.error("Checkout Error:", err);
      toast("Failed to place order. Please try again.", "ERROR");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addressData?.length > 0 && selectedAddressId === null) {
      setSelectedAddressId(addressData[0].id);
    }
  }, [addressData]);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isLoading]);

  const getProductImageUrl = (product) => {
    if (product.images && product.images.length > 0) {
      return apiUrl + "/" + product.images[0].imageUrl;
    }
    return "/default-product.png";
  };

  const getDiscountPrice = (item) => {
  const price = Number(item.ornamentPrice) || 0;
  const beforeGst = Number(item.priceBeforeGst) || 0;
  const discount = Number(item.discount) || 0;

  if (discount > 0) {
    const discountAmount = (beforeGst * discount) / 100;
    const finalPrice = price - discountAmount;
    return Math.round(finalPrice); // round per item
  }

  return Math.round(price);
};

 const totalPrice = Math.round(
  cart.reduce((acc, item) => {
    const discountPrice = getDiscountPrice(item);
    return acc + discountPrice * item.quantity;
  }, 0)
);

  // Check if new address form is complete
  const isNewAddressComplete =
    firstName &&
    lastName &&
    zip &&
    selectedCountry &&
    selectedState &&
    selectedCity &&
    address;

  return (
    <div className="inblog-page">
      <header className="header style7">
        <Topbar />
      </header>

      <div className="main-content main-content-checkout">
        <div className="container">
          <div className="row pt-4 pb-2">
            <div className="col-lg-12">
              <div className="breadcrumb-trail breadcrumbs">
                <ul className="trail-items breadcrumb">
                  <li className="trail-item trail-begin">
                    <Link to="/" className="text-decoration-none">
                      Home
                    </Link>
                  </li>
                  <li className="trail-item trail-end active">Check Out</li>
                </ul>
              </div>
            </div>
          </div>

          <h3 className="custom_blog_title text-center mb-5">Checkout</h3>

          <div className="checkout-wrapper">
            <div className="shipping-section">
              {/* LEFT: ADDRESS SELECTION */}
              <div className="address-section">
                <h3 className="section-title">Shipping Address</h3>

                {/* SAVED ADDRESSES */}
                {addressData?.length > 0 && (
                  <div className="saved-addresses">
                    <h5>Saved Addresses</h5>
                    <div className="address-list">
                      {addressData?.map((addr) => (
                        <div
                          key={addr.id}
                          className={`address-card ${
                            selectedAddressId === addr.id ? "selected" : ""
                          }`}
                          onClick={() => setSelectedAddressId(addr.id)}
                        >
                          <input
                            type="radio"
                            name="address"
                            checked={selectedAddressId === addr.id}
                            onChange={() => setSelectedAddressId(addr.id)}
                          />
                          <div style={{ fontSize: "16px" }}>
                            <div className="address-name">{addr.name}</div>
                            <div className="address-detail">
                              {addr.address}, {addr.city}, {addr.state},{" "}
                              {addr.country} - {addr.zip_code}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* NEW ADDRESS FORM */}
                <div className="new-address-form">
                  <h5>Add New Address</h5>
                  <div className="form-grid">
                    <div className="form-row">
                      <div className="form-group">
                        <label className="fs-5">First Name</label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="form-control fs-5 rounded"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="fs-5">Last Name</label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="form-control fs-5 rounded"
                          required
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="form-group col-lg-4">
                        <label className="fs-5">Country</label>
                        <select
                          value={selectedCountry}
                          onChange={(e) => {
                            setSelectedCountry(e.target.value);
                            setSelectedState("");
                            setSelectedCity("");
                          }}
                          className="form-select fs-5"
                          required
                        >
                          <option value="">Select Country</option>
                          {Object.keys(countryState).map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group col-lg-4">
                        <label className="fs-5">State</label>
                        <select
                          value={selectedState}
                          onChange={(e) => {
                            setSelectedState(e.target.value);
                            setSelectedCity("");
                          }}
                          disabled={!selectedCountry}
                          className="form-select fs-5"
                          required
                        >
                          <option value="">Select State</option>
                          {selectedCountry &&
                            countryState[selectedCountry].map((state) => (
                              <option key={state} value={state}>
                                {state}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="form-group col-lg-4">
                        <label className="fs-5">City</label>
                        <select
                          value={selectedCity}
                          onChange={(e) => setSelectedCity(e.target.value)}
                          disabled={!selectedState}
                          className="form-select fs-5"
                          required
                        >
                          <option value="">Select City</option>
                          {selectedState &&
                            stateCity[selectedState].map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="fs-5">Zip Code</label>
                        <input
                          type="text"
                          value={zip}
                          onChange={(e) => setZip(e.target.value)}
                          className="form-control fs-5"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="fs-5">Address</label>
                        <input
                          type="text"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="form-control fs-5"
                          required
                        />
                      </div>
                    </div>

                    <button
                      style={{ padding: "10px", fontSize: "13px" }}
                      type="button"
                      onClick={handleSaveAddress}
                      className={`btn ${
                        isNewAddressComplete ? "btn-primary" : "btn-secondary"
                      }`}
                      disabled={!isNewAddressComplete}
                    >
                      Save Address
                    </button>
                  </div>
                </div>
              </div>

              {/* RIGHT: ORDER SUMMARY */}
              <div className="order-summary">
                <h3 className="section-title">Your Order</h3>
                <ul className="order-items">
                  {cart.map((item) => {
                    console.log("item", item.discount);
                    const discountPrice =
                    getDiscountPrice(item);

                    return (
                      <li key={item.cartId} className="order-item">
                        <div className="item-image">
                          <img src={getProductImageUrl(item)} alt={item.name} />
                        </div>
                        <div className="item-details">
                          <h5>
                            {item.name} × {item.quantity}
                          </h5>
                          <div className="item-price">
                            {formatINR(
                              (discountPrice * item.quantity),
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="order-total">
                  <span>Total:</span>
                  <span className="total-amount fs-3">
                    {formatINR(Math.round(totalPrice))}
                  </span>
                </div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="checkout-actions">
              <Link
                to="/shopping-cart"
                className="btn btn-outline-secondary back-to-cart"
              >
                ← Back to Cart
              </Link>
              <button
                type="button"
                onClick={handlePlaceOrder}
                disabled={cart.length === 0 || selectedAddressId === null}
                className="btn btn-primary place-order-btn"
              >
                Order Now
              </button>
            </div>
          </div>
        </div>

        {/* LOADING OVERLAY */}
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="spinner"></div>
              <div>Your Order is being processed...</div>
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Mobile Footer */}
      <div className="footer-device-mobile">
        <div className="wapper">
          <div className="footer-device-mobile-item device-home">
            <Link to="/" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-house"></i>
              </span>{" "}
              Home
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-cart">
            <Link to="/wishlist" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-heart"></i>
                <span className="count-icon">{wishlist.length}</span>
              </span>{" "}
              Wishlist
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-cart">
            <Link to="/shopping-cart" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-shopping-cart-simple" />
                <span className="count-icon">{cart.length}</span>
              </span>{" "}
              Cart
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-user">
            <Link to="/profile" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-user"></i>
              </span>{" "}
              Account
            </Link>
          </div>
        </div>
      </div>

      <Link to="#" className="backtotop">
        <i
          className="ph ph-caret-double-up"
          style={{
            color: "white",
            fontSize: "30px",
          }}
        ></i>
      </Link>

      {/* Responsive Styles */}
      <style jsx>{`
        .checkout-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: "sans-serif";
          background: #fafafa;
          border-radius: 16px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
        }

        .shipping-section {
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 30px;
        }

        .address-section,
        .order-summary {
          background: #fff;
          padding: 32px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          border: 1px solid #eee;
        }

        .order-summary {
          position: sticky;
          top: 20px;
          height: fit-content;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d2d2d;
          margin-bottom: 24px;
          padding-bottom: 12px;
          border-bottom: 2px solid #f8f0d8;
        }

        .saved-addresses h5,
        .new-address-form h5 {
          font-weight: 600;
          margin: 24px 0 16px;
          color: #333;
        }

        .address-list {
          max-height: 300px;
          overflow-y: auto;
          padding: 16px;
          border: 1px solid #eee;
          border-radius: 12px;
        }

        .address-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border: 1px solid #eee;
          border-radius: 10px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #fff;
        }

        .address-card.selected {
          background: #fff8e6;
        }

        .address-card input[type="radio"] {
          margin-top: 4px;
        }

        .address-name {
          font-weight: 600;
          color: #333;
        }

        .address-detail {
          font-size: 0.9rem;
          color: #666;
        }

        .form-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .country-state-city {
          grid-template-columns: repeat(3, 1fr);
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          margin-bottom: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #555;
        }

        .form-control,
        .form-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        /* Order Summary */
        .order-items {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .order-item {
          display: flex;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid #f0f0f0;
          align-items: center;
        }

        .item-image img {
          width: 70px;
          height: 70px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .item-details h5 {
          margin: 0 0 8px 0;
          font-size: 1.5rem;
          font-weight: 500;
        }

        .item-price {
          font-size: 1.85rem;
          font-weight: 600;
          color: #d4af37;
        }

        .order-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding-top: 16px;
          border-top: 2px solid #f8f0d8;
          font-size: 1.25rem;
          font-weight: 700;
          color: #2d2d2d;
        }

        .total-amount {
          color: #d4af37;
        }

        /* Action Buttons */
        .checkout-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
          gap: 16px;
          flex-wrap: wrap;
        }

        .back-to-cart,
        .place-order-btn {
          flex-grow: 1;
          max-width: 200px;
          text-align: center;
          padding: 10px 20px;
          font-size: 1.3rem;
          border-radius: 12px;
        }

        .place-order-btn:disabled {
          background-color: #121535;
          color: white;
          cursor: not-allowed;
        }

        /* Loading Overlay */
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 255, 255, 0.85);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          backdrop-filter: blur(4px);
        }

        .loading-content {
          text-align: center;
          padding: 40px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          animation: fadeInUp 0.4s ease;
        }

        .spinner {
          width: 60px;
          height: 60px;
          margin: 0 auto 20px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #d4af37;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        /* Responsive: Tablet & Mobile */
        @media (max-width: 992px) {
          .shipping-section {
            grid-template-columns: 1fr;
          }

          .order-summary {
            position: static;
          }

          .country-state-city {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .checkout-actions {
            flex-direction: column;
          }

          .back-to-cart,
          .place-order-btn {
            max-width: 100%;
          }
        }

        @media (max-width: 576px) {
          .checkout-wrapper {
            padding: 15px;
          }

          .address-section,
          .order-summary {
            padding: 20px;
          }

          .section-title {
            font-size: 1.25rem;
          }
        }

        /* Animations */
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CheckOut;
